import {useMaterialSuggestions} from '@/hooks/useMaterialSuggestions';
import { cacheOperatorRows, readOperatorRows } from "@/lib/operator-drafts";
import { useOperatorDraft } from "@/hooks/useOperatorDraft";
import { queueScope } from "@/lib/offline-queue";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useOffline } from "@/contexts/OfflineContext";
import { apiFetch } from "@/lib/api-fetch";
import { hapticSuccess } from "@/lib/native-init";
import {
  Package,
  Plus,
  ArrowLeft,
  Search,
  Filter,
  ArrowUpDown,
  FolderTree,
  Check,
  Weight,
  Layers,
  Pencil,
  Trash2,
  X,
  Save,
} from "lucide-react";

type WindrowOption = {
  id: number;
  row_id: string;
  name: string;
  status: string;
};

type MaterialRecord = {
  id: number;
  windrow_id: number;
  windrow_name: string;
  windrow_row_id: string;
  material_name: string;
  cubic_yards: number | null;
  weight: number | null;
  notes: string | null;
  added_at: string;
};

type SortBy = "added_at" | "material_name" | "weight" | "windrow_name";
type GroupBy = "none" | "windrow" | "day" | "material";

const COMMON_MATERIALS = [
  "Green Waste",
  "Food Waste",
  "Wood Chips",
  "Biosolids",
  "Manure",
  "Agricultural Waste",
  "Paper / Cardboard",
  "Sawdust",
  "Straw / Hay",
  "Finished Compost",
];

function formatDateTime(dateString: string) {
  return new Date(dateString).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export default function LogMaterialPage() {
  const location = useLocation();
  const isOperatorView = location.pathname.startsWith("/app/operator");

  if (isOperatorView) {
    return <OperatorMaterialLogger />;
  }

  return <ManagerMaterialWorkspace />;
}

/* ═══════════════════════ MANAGER VIEW ═══════════════════════ */

function ManagerMaterialWorkspace() {
  const materialCatalog = useMaterialSuggestions(COMMON_MATERIALS);
  const materialSuggestions=materialCatalog.options;
  const { token } = useAuth();
  const [searchParams] = useSearchParams();
  const preselectedWindrow = searchParams.get("windrow");

  const [records, setRecords] = useState<MaterialRecord[]>([]);
  const [windrows, setWindrows] = useState<WindrowOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [windrowId, setWindrowId] = useState(preselectedWindrow || "");
  const [materialName, setMaterialName] = useState("");
  const [customMaterial,setCustomMaterial]=useState("");
  const [weight, setWeight] = useState("");
  const [cubicYards, setCubicYards] = useState("");
  const [notes, setNotes] = useState("");

  const [search, setSearch] = useState("");
  const [windrowFilter, setWindrowFilter] = useState("all");
  const [sortBy, setSortBy] = useState<SortBy>("added_at");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
  const [groupBy, setGroupBy] = useState<GroupBy>("none");

  // Edit state
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editMaterial, setEditMaterial] = useState("");
  const [editWeight, setEditWeight] = useState("");
  const [editCubicYards, setEditCubicYards] = useState("");
  const [editNotes, setEditNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState<number | null>(null);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [materialsRes, windrowsRes] = await Promise.all([
        apiFetch("/api/materials", { headers: { Authorization: `Bearer ${token}` } }),
        apiFetch("/api/windrows/active", { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      if (!materialsRes.ok) throw new Error(`Failed to load materials (${materialsRes.status})`);
      if (!windrowsRes.ok) throw new Error(`Failed to load windrows (${windrowsRes.status})`);

      const materialsData = await materialsRes.json();
      const windrowsData = await windrowsRes.json();
      const nextWindrows = windrowsData.windrows || [];
      setRecords(materialsData.materials || []);
      setWindrows(nextWindrows);
      if (!windrowId && !preselectedWindrow && nextWindrows.length > 0) {
        setWindrowId(String(nextWindrows[0].id));
      }
    } catch (err: any) {
      setError(err?.message || "Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [token]);

  const stats = useMemo(() => {
    const total = records.length;
    const totalTons = records.reduce((s, r) => s + (r.weight || 0), 0);
    const totalYards = records.reduce((s, r) => s + (r.cubic_yards || 0), 0);
    const uniqueWindrows = new Set(records.map((r) => r.windrow_id)).size;
    return { total, totalTons: Math.round(totalTons * 10) / 10, totalYards: Math.round(totalYards * 10) / 10, uniqueWindrows };
  }, [records]);

  const sortedRows = useMemo(() => {
    const searchLower = search.trim().toLowerCase();
    const filtered = records.filter((r) => {
      if (windrowFilter !== "all" && String(r.windrow_id) !== windrowFilter) return false;
      if (!searchLower) return true;
      return [r.windrow_name, r.windrow_row_id, r.material_name, r.notes || ""]
        .join(" ")
        .toLowerCase()
        .includes(searchLower);
    });
    return [...filtered].sort((a, b) => {
      let left: string | number, right: string | number;
      if (sortBy === "added_at") {
        left = new Date(a.added_at).getTime();
        right = new Date(b.added_at).getTime();
      } else if (sortBy === "weight") {
        left = a.weight || 0;
        right = b.weight || 0;
      } else if (sortBy === "windrow_name") {
        left = a.windrow_name;
        right = b.windrow_name;
      } else {
        left = a.material_name;
        right = b.material_name;
      }
      const cmp = typeof left === "number" && typeof right === "number" ? left - right : String(left).localeCompare(String(right));
      return sortDirection === "asc" ? cmp : -cmp;
    });
  }, [records, search, windrowFilter, sortBy, sortDirection]);

  const groupedRows = useMemo(() => {
    if (groupBy === "none") return [{ key: "all", label: `All Records (${sortedRows.length})`, rows: sortedRows }];
    const map = new Map<string, MaterialRecord[]>();
    for (const row of sortedRows) {
      let key = "";
      if (groupBy === "windrow") key = `${row.windrow_name} (${row.windrow_row_id})`;
      else if (groupBy === "day") key = row.added_at.slice(0, 10);
      else key = row.material_name;
      const g = map.get(key);
      if (g) g.push(row);
      else map.set(key, [row]);
    }
    return Array.from(map.entries()).map(([key, rows]) => ({
      key,
      label:
        groupBy === "day"
          ? `${new Date(`${key}T00:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })} (${rows.length})`
          : `${key} (${rows.length})`,
      rows,
    }));
  }, [sortedRows, groupBy]);

  const handleAddRecord = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!windrowId || !materialName || !weight) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await apiFetch(`/api/windrows/${windrowId}/materials`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          materialName: materialSuggestions.find(o=>o.key===materialName)?.name || (materialName==='Other'?customMaterial:materialName),
          ingredientId: materialSuggestions.find(o=>o.key===materialName)?.ingredientId ?? null,
          weight: Number(weight),
          cubicYards: cubicYards ? Number(cubicYards) : null,
          notes: notes || null,
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || `Failed to add material (${res.status})`);
      }
      setMaterialName("");
      setWeight("");
      setCubicYards("");
      setNotes("");
      await loadData();
    } catch (err: any) {
      setError(err?.message || "Failed to add material");
    } finally {
      setSubmitting(false);
    }
  };

  const startEditing = (r: MaterialRecord) => {
    setEditingId(r.id);
    setEditMaterial(r.material_name);
    setEditWeight(r.weight != null ? String(r.weight) : "");
    setEditCubicYards(r.cubic_yards != null ? String(r.cubic_yards) : "");
    setEditNotes(r.notes || "");
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditMaterial("");
    setEditWeight("");
    setEditCubicYards("");
    setEditNotes("");
  };

  const handleSaveEdit = async () => {
    if (!editingId || !editMaterial) return;
    setSaving(true);
    setError(null);
    try {
      const res = await apiFetch(`/api/materials/${editingId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          materialName: editMaterial,
          weight: editWeight ? Number(editWeight) : null,
          cubicYards: editCubicYards ? Number(editCubicYards) : null,
          notes: editNotes || null,
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || `Failed to update (${res.status})`);
      }
      cancelEditing();
      await loadData();
    } catch (err: any) {
      setError(err?.message || "Failed to update material");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm("Delete this material record? This cannot be undone.")) return;
    setDeleting(id);
    setError(null);
    try {
      const res = await apiFetch(`/api/materials/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || `Failed to delete (${res.status})`);
      }
      await loadData();
    } catch (err: any) {
      setError(err?.message || "Failed to delete material");
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div className="space-y-6 max-w-7xl">
      <div>
        <h1 className="heading-display text-3xl text-foreground">Material Records</h1>
        <p className="text-sm text-muted-foreground mt-1">Track materials added to each windrow with tonnage.</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-card border border-border rounded-xl p-4">
          <p className="label-caps text-muted-foreground">Total Additions</p>
          <p className="stat-value text-3xl text-foreground mt-2">{stats.total}</p>
        </div>
        <div className="bg-card border border-border rounded-xl p-4">
          <p className="label-caps text-muted-foreground">Total Tonnage</p>
          <p className="stat-value text-3xl text-foreground mt-2">{stats.totalTons} t</p>
        </div>
        <div className="bg-card border border-border rounded-xl p-4">
          <p className="label-caps text-muted-foreground">Total Volume</p>
          <p className="stat-value text-3xl text-foreground mt-2">{stats.totalYards} yd³</p>
        </div>
        <div className="bg-card border border-border rounded-xl p-4">
          <p className="label-caps text-muted-foreground">Active Windrows</p>
          <p className="stat-value text-3xl text-foreground mt-2">{stats.uniqueWindrows}</p>
        </div>
      </div>

      <form onSubmit={handleAddRecord} className="bg-card border border-border rounded-xl p-5 space-y-4">
        <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
          <Plus size={16} className="text-forest-600" />
          Add Material
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-3">
          <select
            value={windrowId}
            onChange={(e) => setWindrowId(e.target.value)}
            className="lg:col-span-2 px-3 py-2.5 bg-background border border-border rounded-lg text-sm"
            required
          >
            <option value="">Select windrow</option>
            {windrows.map((w) => (
              <option key={w.id} value={w.id}>
                {w.name} ({w.row_id})
              </option>
            ))}
          </select>

          <button type="button" onClick={()=>materialCatalog.refresh()} className="text-xs text-forest-600">Refresh ingredients</button>
          {materialCatalog.error&&<p role="alert" className="text-sm text-amber-700">{materialCatalog.error}</p>}
          <select
            value={materialName}
            onChange={(e) => setMaterialName(e.target.value)}
            className="px-3 py-2.5 bg-background border border-border rounded-lg text-sm"
            required
          >
            <option value="">Ingredient / saved material</option>
            {materialSuggestions.map((m) => (
              <option key={m.key} value={m.key}>
                {m.name}{m.source?` · ${m.source}`:''}
              </option>
            ))}
            <option value="Other">Other</option>
          </select>

          {materialName==='Other'&&<input aria-label="Custom material name" required value={customMaterial} onChange={e=>setCustomMaterial(e.target.value)} className="border border-border rounded-lg p-2 bg-background" placeholder="Custom material name"/>}
          <input
            type="number"
            min="0"
            step="0.1"
            value={weight}
            onChange={(e) => setWeight(e.target.value)}
            placeholder="US tons *"
            className="px-3 py-2.5 bg-background border border-border rounded-lg text-sm"
            required
          />

          <input
            type="number"
            min="0"
            step="0.1"
            value={cubicYards}
            onChange={(e) => setCubicYards(e.target.value)}
            placeholder="Cu. Yards"
            className="px-3 py-2.5 bg-background border border-border rounded-lg text-sm"
          />

          <button
            type="submit"
            disabled={submitting || !windrowId || !materialName || !weight}
            className="px-4 py-2.5 bg-forest-600 hover:bg-forest-700 disabled:opacity-50 text-white rounded-lg text-sm font-semibold"
          >
            {submitting ? "Saving..." : "Save"}
          </button>
        </div>
        <input
          type="text"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Notes (optional)"
          className="w-full px-3 py-2.5 bg-background border border-border rounded-lg text-sm"
        />
      </form>

      {/* Filters */}
      <div className="bg-card border border-border rounded-xl p-5 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
          <label className="lg:col-span-2 flex items-center gap-2 px-3 bg-background border border-border rounded-lg">
            <Search size={15} className="text-muted-foreground" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search material, windrow, notes..."
              className="w-full py-2 text-sm bg-transparent outline-none"
            />
          </label>
          <select value={windrowFilter} onChange={(e) => setWindrowFilter(e.target.value)} className="px-3 py-2 bg-background border border-border rounded-lg text-sm">
            <option value="all">All windrows</option>
            {windrows.map((w) => (
              <option key={w.id} value={w.id}>{w.name} ({w.row_id})</option>
            ))}
          </select>
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value as SortBy)} className="px-3 py-2 bg-background border border-border rounded-lg text-sm">
            <option value="added_at">Sort by date</option>
            <option value="weight">Sort by tonnage</option>
            <option value="material_name">Sort by material</option>
            <option value="windrow_name">Sort by windrow</option>
          </select>
          <button
            type="button"
            onClick={() => setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"))}
            className="px-3 py-2 bg-background border border-border rounded-lg text-sm inline-flex items-center justify-center gap-1"
          >
            <ArrowUpDown size={14} />
            {sortDirection === "asc" ? "Ascending" : "Descending"}
          </button>
        </div>
        <div className="flex items-center gap-2">
          <FolderTree size={14} className="text-muted-foreground" />
          <select value={groupBy} onChange={(e) => setGroupBy(e.target.value as GroupBy)} className="px-3 py-2 bg-background border border-border rounded-lg text-sm">
            <option value="none">No grouping</option>
            <option value="windrow">Group by windrow</option>
            <option value="day">Group by day</option>
            <option value="material">Group by material</option>
          </select>
          <span className="text-xs text-muted-foreground inline-flex items-center gap-1">
            <Filter size={12} />
            {sortedRows.length} matching records
          </span>
        </div>
      </div>

      {/* Table */}
      <div className="bg-card border border-border rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-muted-foreground">Loading records...</div>
        ) : error ? (
          <div className="p-12 text-center text-red-600 dark:text-red-400">{error}</div>
        ) : sortedRows.length === 0 ? (
          <div className="p-12 text-center text-muted-foreground">No material records found.</div>
        ) : (
          <div className="divide-y divide-border">
            {groupedRows.map((group) => (
              <section key={group.key} className="p-4">
                {groupBy !== "none" && <h3 className="text-sm font-semibold text-foreground mb-3">{group.label}</h3>}
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
                        <th className="px-3 py-2.5">Added At</th>
                        <th className="px-3 py-2.5">Windrow</th>
                        <th className="px-3 py-2.5">Material</th>
                        <th className="px-3 py-2.5">Tonnage</th>
                        <th className="px-3 py-2.5">Cu. Yards</th>
                        <th className="px-3 py-2.5">Notes</th>
                        <th className="px-3 py-2.5 w-24"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {group.rows.map((r) =>
                        editingId === r.id ? (
                          <tr key={r.id} className="border-b border-border/60 last:border-0 bg-amber-50/50 dark:bg-amber-900/5">
                            <td className="px-3 py-2.5 text-muted-foreground whitespace-nowrap">{formatDateTime(r.added_at)}</td>
                            <td className="px-3 py-2.5">
                              <p className="font-medium text-foreground">{r.windrow_name}</p>
                              <p className="text-xs text-muted-foreground font-mono">{r.windrow_row_id}</p>
                            </td>
                            <td className="px-3 py-1.5">
                              <select
                                value={materialSuggestions.some(o=>o.name===editMaterial) ? editMaterial : "Other"}
                                onChange={(e) => setEditMaterial(e.target.value === "Other" ? editMaterial : e.target.value)}
                                className="w-full px-2 py-1.5 bg-background border border-border rounded-lg text-xs"
                              >
                                {materialSuggestions.map((m) => (
                                  <option key={m.key} value={m.name}>{m.name}</option>
                                ))}
                                <option value="Other">Other</option>
                              </select>
                              {!materialSuggestions.some(o=>o.name===editMaterial) && (
                                <input
                                  type="text"
                                  value={editMaterial}
                                  onChange={(e) => setEditMaterial(e.target.value)}
                                  className="w-full mt-1 px-2 py-1.5 bg-background border border-border rounded-lg text-xs"
                                  placeholder="Custom material..."
                                />
                              )}
                            </td>
                            <td className="px-3 py-1.5">
                              <input
                                type="number"
                                step="0.1"
                                min="0"
                                value={editWeight}
                                onChange={(e) => setEditWeight(e.target.value)}
                                className="w-20 px-2 py-1.5 bg-background border border-border rounded-lg text-xs"
                              />
                            </td>
                            <td className="px-3 py-1.5">
                              <input
                                type="number"
                                step="0.1"
                                min="0"
                                value={editCubicYards}
                                onChange={(e) => setEditCubicYards(e.target.value)}
                                className="w-20 px-2 py-1.5 bg-background border border-border rounded-lg text-xs"
                              />
                            </td>
                            <td className="px-3 py-1.5">
                              <input
                                type="text"
                                value={editNotes}
                                onChange={(e) => setEditNotes(e.target.value)}
                                className="w-full px-2 py-1.5 bg-background border border-border rounded-lg text-xs"
                                placeholder="Notes..."
                              />
                            </td>
                            <td className="px-3 py-1.5">
                              <div className="flex items-center gap-1">
                                <button
                                  onClick={handleSaveEdit}
                                  disabled={saving || !editMaterial}
                                  className="p-1.5 text-forest-600 hover:bg-forest-100 dark:hover:bg-forest-900/20 rounded-lg transition-colors disabled:opacity-50"
                                  title="Save"
                                >
                                  <Save size={14} />
                                </button>
                                <button
                                  onClick={cancelEditing}
                                  className="p-1.5 text-muted-foreground hover:bg-muted rounded-lg transition-colors"
                                  title="Cancel"
                                >
                                  <X size={14} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ) : (
                          <tr key={r.id} className="border-b border-border/60 last:border-0 group/row hover:bg-muted/30 transition-colors">
                            <td className="px-3 py-2.5 text-muted-foreground whitespace-nowrap">{formatDateTime(r.added_at)}</td>
                            <td className="px-3 py-2.5">
                              <p className="font-medium text-foreground">{r.windrow_name}</p>
                              <p className="text-xs text-muted-foreground font-mono">{r.windrow_row_id}</p>
                            </td>
                            <td className="px-3 py-2.5">
                              <span className="inline-flex items-center gap-1.5 px-2 py-0.5 bg-amber-100 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 rounded-full text-xs font-semibold">
                                <Package size={10} />
                                {r.material_name}
                              </span>
                            </td>
                            <td className="px-3 py-2.5 font-semibold text-foreground">{r.weight != null ? `${r.weight} t` : "-"}</td>
                            <td className="px-3 py-2.5 text-muted-foreground">{r.cubic_yards != null ? `${r.cubic_yards} yd³` : "-"}</td>
                            <td className="px-3 py-2.5 text-muted-foreground max-w-xs truncate">{r.notes || "-"}</td>
                            <td className="px-3 py-2.5">
                              <div className="flex items-center gap-1 opacity-0 group-hover/row:opacity-100 transition-opacity">
                                <button
                                  onClick={() => startEditing(r)}
                                  className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors"
                                  title="Edit"
                                >
                                  <Pencil size={13} />
                                </button>
                                <button
                                  onClick={() => handleDelete(r.id)}
                                  disabled={deleting === r.id}
                                  className="p-1.5 text-muted-foreground hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/10 rounded-lg transition-colors disabled:opacity-50"
                                  title="Delete"
                                >
                                  <Trash2 size={13} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        )
                      )}
                    </tbody>
                  </table>
                </div>
              </section>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ═══════════════════════ OPERATOR VIEW ═══════════════════════ */

function OperatorMaterialLogger() {
  const materialCatalog = useMaterialSuggestions(COMMON_MATERIALS);
  const materialSuggestions=materialCatalog.options;
  const { token, user } = useAuth();
  const { queueAction } = useOffline();
  const [submitError, setSubmitError] = useState<string | null>(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedWindrow = searchParams.get("windrow");

  const [windrows, setWindrows] = useState<WindrowOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const [windrowId, setWindrowId] = useState(preselectedWindrow || "");
  const draft = useOperatorDraft(queueScope(user), "material", windrowId, {"materialName": "", "customMaterial": "", "weight": "", "cubicYards": "", "notes": ""});
  const materialName = draft.fields.materialName;
  const setMaterialName = (value: string) => draft.setField("materialName", value);
  const customMaterial = draft.fields.customMaterial;
  const setCustomMaterial = (value: string) => draft.setField("customMaterial", value);
  const weight = draft.fields.weight;
  const setWeight = (value: string) => draft.setField("weight", value);
  const cubicYards = draft.fields.cubicYards;
  const setCubicYards = (value: string) => draft.setField("cubicYards", value);
  const notes = draft.fields.notes;
  const setNotes = (value: string) => draft.setField("notes", value);


  useEffect(() => {
    let cancelled = false;
    const scope = queueScope(user);
    const show = (rows: WindrowOption[]) => {
      if (cancelled) return;
      const active = rows.filter(w => w.status === 'active');
      setWindrows(active);
      if (!preselectedWindrow && active.length) setWindrowId(current => current || String(active[0].id));
    };
    void readOperatorRows(scope).then(rows => show(rows as WindrowOption[])).catch(() => {});
    apiFetch('/api/windrows', { headers: { Authorization: `Bearer ${token}` } })
      .then(async response => { if (!response.ok) throw new Error('Unable to load rows'); return response.json(); })
      .then(data => { show(data.windrows || []); void cacheOperatorRows(scope, data.windrows || []).catch(() => {}); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [token, preselectedWindrow, user?.id, user?.organization_id]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const selectedIngredient=materialSuggestions.find(o=>o.key===materialName);
    const finalName = materialName === 'Other' ? customMaterial : selectedIngredient?.name || materialName;
    if (!windrowId || !finalName || !weight || submitting || !draft.ready) return;
    setSubmitting(true); setSubmitError(null);
    try {
      await queueAction('material', Number(windrowId), {
        materialName: finalName, ingredientId:selectedIngredient?.ingredientId??null, weight: Number(weight),
        cubicYards: cubicYards ? Number(cubicYards) : null, notes: notes || null,
      });
      await draft.clear().catch(() => {});
      setSuccess(true);
      setTimeout(() => navigate(`/app/operator/windrows/${windrowId}`), 1500);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Unable to save on this device. Your form is still here.');
    } finally { setSubmitting(false); }
  };

  if (success) {
    return (
      <div className="max-w-md mx-auto mt-16 text-center">
        <div className="w-20 h-20 bg-forest-100 dark:bg-forest-900/30 rounded-2xl flex items-center justify-center mx-auto mb-5">
          <Check size={36} className="text-forest-600 dark:text-forest-400" />
        </div>
        <h2 className="heading-display text-2xl text-foreground mb-2">Material Added</h2>
        <p className="text-sm text-muted-foreground">
          <span className="stat-value text-lg text-foreground">{weight} tons</span> of{" "}
          <span className="font-semibold">{materialName === "Other" ? customMaterial : materialName}</span> saved on this device; upload status appears above
        </p>
        <p className="text-xs text-muted-foreground mt-2">Redirecting to windrow...</p>
      </div>
    );
  }

  return (
    <div className="max-w-lg mx-auto">
      <Link
        to="/app/operator"
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-5"
      >
        <ArrowLeft size={14} />
        Back to Dashboard
      </Link>

      <div className="bg-card border border-border rounded-2xl overflow-hidden shadow-sm">
        <div className="p-6 border-b border-border bg-gradient-to-r from-amber-50 to-amber-50/50 dark:from-amber-900/10 dark:to-transparent">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-amber-100 dark:bg-amber-900/30 rounded-xl flex items-center justify-center">
              <Package size={22} className="text-amber-600 dark:text-amber-400" />
            </div>
            <div>
              <h1 className="heading-display text-xl text-foreground">Add Material</h1>
              <p className="text-xs text-muted-foreground mt-0.5">Record material added to a windrow</p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6"><fieldset disabled={!!windrowId && (!draft.ready || submitting)} className="space-y-6">
          <div className="flex items-center justify-between gap-3 text-sm"><span role="status">{draft.status}</span>{draft.status && <button type="button" className="min-h-11 underline" onClick={() => { if (window.confirm('Discard this unfinished entry and its photo?')) void draft.discard().catch(() => setSubmitError('Could not discard the draft. Try again.')); }}>Discard draft</button>}</div>
          {submitError && <p role="alert" className="text-sm text-red-600">{submitError}</p>}
          {/* Select windrow */}
          <div>
            <label className="label-caps text-muted-foreground mb-2 block">Select Windrow *</label>
            {loading ? (
              <div className="h-11 bg-muted rounded-xl animate-pulse" />
            ) : (
              <select
                value={windrowId}
                onChange={(e) => setWindrowId(e.target.value)}
                className="w-full px-3 py-2.5 bg-background border border-border rounded-xl text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-forest-500/50"
              >
                <option value="">Choose a windrow...</option>
                {windrows.map((w) => (
                  <option key={w.id} value={w.id}>{w.name} ({w.row_id})</option>
                ))}
              </select>
            )}
          </div>

          <button type="button" onClick={()=>materialCatalog.refresh()} className="text-sm text-forest-600">Refresh ingredients</button>{materialCatalog.error&&<p role="alert" className="text-sm text-amber-700">{materialCatalog.error}</p>}
          {/* Material type selector */}
          <div>
            <label className="label-caps text-muted-foreground mb-2 block">Material Type *</label>
            <div className="grid grid-cols-2 gap-2">
              {materialSuggestions.map((m) => (
                <button
                  key={m.key}
                  type="button"
                  onClick={() => setMaterialName(m.key)}
                  className={`px-3 py-3 text-xs font-semibold rounded-xl transition-all text-left flex items-center gap-2 ${
                    materialName === m.key
                      ? "bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 border-2 border-amber-400 dark:border-amber-600"
                      : "bg-secondary text-muted-foreground hover:text-foreground border-2 border-transparent hover:border-border"
                  }`}
                >
                  <Package size={14} className={materialName === m.key ? "text-amber-600" : "text-muted-foreground/50"} />
                  {m.name}{m.source?` · ${m.source}`:''}
                </button>
              ))}
              <button
                type="button"
                onClick={() => setMaterialName("Other")}
                className={`px-3 py-3 text-xs font-semibold rounded-xl transition-all text-left flex items-center gap-2 col-span-2 ${
                  materialName === "Other"
                    ? "bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 border-2 border-amber-400 dark:border-amber-600"
                    : "bg-secondary text-muted-foreground hover:text-foreground border-2 border-transparent hover:border-border"
                }`}
              >
                <Plus size={14} className={materialName === "Other" ? "text-amber-600" : "text-muted-foreground/50"} />
                Other (custom)
              </button>
            </div>
            {materialName === "Other" && (
              <input
                type="text"
                placeholder="Enter material name..."
                value={customMaterial}
                onChange={(e) => setCustomMaterial(e.target.value)}
                className="w-full mt-2 px-3 py-2.5 bg-background border border-border rounded-xl text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-forest-500/50"
                required
              />
            )}
          </div>

          {/* Tonnage (primary) */}
          <div>
            <label className="label-caps text-muted-foreground mb-2 flex items-center gap-1.5">
              <Weight size={11} />
              Tonnage *
            </label>
            <div className="relative">
              <input
                type="number"
                step="0.1"
                min="0"
                max="500"
                placeholder="0.0"
                value={weight}
                onChange={(e) => setWeight(e.target.value)}
                className="w-full px-4 py-5 stat-value text-4xl text-center border-2 border-border bg-background text-foreground rounded-2xl focus:outline-none focus:ring-2 focus:ring-forest-500/50 focus:border-forest-500 transition-colors"
                required
              />
              {weight && (
                <div className="absolute right-5 top-1/2 -translate-y-1/2">
                  <span className="text-xl text-muted-foreground/50">tons</span>
                </div>
              )}
            </div>
          </div>

          {/* Cubic Yards (optional) */}
          <div>
            <label className="label-caps text-muted-foreground mb-2 flex items-center gap-1.5">
              <Layers size={11} />
              Volume (optional)
            </label>
            <div className="relative">
              <input
                type="number"
                step="0.1"
                min="0"
                max="1000"
                placeholder="0.0"
                value={cubicYards}
                onChange={(e) => setCubicYards(e.target.value)}
                className="w-full px-4 py-3 text-lg text-center border border-border bg-background text-foreground rounded-xl focus:outline-none focus:ring-2 focus:ring-forest-500/50 transition-colors"
              />
              {cubicYards && (
                <div className="absolute right-4 top-1/2 -translate-y-1/2">
                  <span className="text-sm text-muted-foreground/50">yd³</span>
                </div>
              )}
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="label-caps text-muted-foreground mb-2 block">Notes (optional)</label>
            <textarea
              rows={2}
              placeholder="Source, quality, observations..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="w-full px-3 py-2.5 bg-background border border-border rounded-xl text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-2 focus:ring-forest-500/50 resize-none"
            />
          </div>

          <button
            type="submit"
            disabled={!windrowId || !materialName || (materialName === "Other" && !customMaterial) || !weight || submitting}
            className="w-full py-3.5 bg-forest-600 hover:bg-forest-700 disabled:opacity-50 text-white text-sm font-bold rounded-xl transition-colors shadow-sm shadow-forest-600/20"
          >
            {submitting ? "Adding..." : "Add Material"}
          </button>
        </fieldset></form>
      </div>
    </div>
  );
}
